<template>
  <div class="sel__filter" :class="$attrs.class" ref="container">
    <label v-if="label" class="form-label" :for="id">{{ label }} <span v-if="required" class="text-red-500">*</span></label>
    <div class="sel__input-wrapper">
      <input
        :id="id"
        ref="input"
        class="form-select sel__input"
        :class="{ error }"
        type="text"
        v-bind="{ ...$attrs, class: null }"
        :placeholder="placeholder"
        :disabled="$attrs.disabled"
        @input="handleInput"
        @focus="handleFocus"
        @blur="handleBlur"
        @keydown.down.prevent="onArrowDown"
        @keydown.up.prevent="onArrowUp"
        @keydown.enter.prevent="selectCurrentSelection"
        @keydown.escape="isListVisible = false"
        autocomplete="off"
      />
      <button
        v-if="selectedLabel && !$attrs.disabled"
        type="button"
        class="sel__clear-btn"
        @mousedown.prevent="clearSelection"
      >
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" width="14" height="14">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>

      <transition name="dropdown">
        <div v-show="isListVisible" class="i__filter__list">
          <div v-if="isLoading" class="i__filter__state">
            <span class="i__filter__spinner"></span>
            <span>Mencari...</span>
          </div>
          <ul v-else-if="items.length">
            <li
              v-for="(item, index) in items"
              :key="item.id ?? index"
              :class="{ 'i__filter__active': index === activeIndex }"
              @mousedown.prevent="selectItem(item)"
            >
              {{ item.name }}
            </li>
          </ul>
          <div v-else class="i__filter__state">
            Tidak ada hasil
          </div>
        </div>
      </transition>
    </div>
    <div v-if="error" class="form-error">{{ error }}</div>
  </div>
</template>

<script>
import { v4 as uuid } from 'uuid'

export default {
  inheritAttrs: false,
  props: {
    placeholder: { type: String, default: '' },
    onInput:     { type: Function },
    items:       { type: Array, default: () => [] },
    id:          { type: String, default: () => `sif-${uuid()}` },
    error:       String,
    label:       String,
    required:    { type: Boolean, default: false },
    modelValue:  [String, Number, Boolean],
  },
  emits: ['update:modelValue', 'focus'],
  data() {
    return {
      selected:      this.modelValue,
      selectedLabel: '',
      isListVisible: false,
      isLoading:     false,
      activeIndex:   -1,
    }
  },
  watch: {
    selected(val) {
      this.$emit('update:modelValue', val)
    },
    modelValue(val) {
      this.selected = val
      if (!val) {
        this.selectedLabel = ''
        if (this.$refs.input) this.$refs.input.value = ''
      } else {
        const item = this.items.find(i => String(i.id) === String(val))
        if (item) {
          this.selectedLabel = item.name
          if (this.$refs.input) this.$refs.input.value = item.name
        }
      }
    },
    items: {
      deep: true,
      handler() {
        this.isLoading = false
        this.activeIndex = -1
      },
    },
  },
  mounted() {
    document.addEventListener('click', this.onOutsideClick)
    if (this.modelValue) {
      const item = this.items.find(i => String(i.id) === String(this.modelValue))
      if (item) {
        this.selectedLabel = item.name
        if (this.$refs.input) this.$refs.input.value = item.name
      }
    }
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onOutsideClick)
  },
  methods: {
    onOutsideClick(e) {
      if (!this.$el.contains(e.target)) {
        this.isListVisible = false
      }
    },
    handleFocus(e) {
      if (this.selectedLabel && this.$refs.input) {
        this.$refs.input.value = ''
      }
      this.isListVisible = true
      this.$emit('focus', e)
      if (this.onInput) this.onInput(e)
    },
    handleBlur() {
      if (this.selectedLabel && this.$refs.input) {
        this.$refs.input.value = this.selectedLabel
      }
    },
    handleInput(e) {
      this.isLoading = true
      this.isListVisible = true
      if (this.onInput) this.onInput(e)
    },
    selectItem(item) {
      this.selected      = item.id
      this.selectedLabel = item.name
      this.isListVisible = false
      if (this.$refs.input) this.$refs.input.value = item.name
    },
    clearSelection() {
      this.selected      = null
      this.selectedLabel = ''
      this.isListVisible = false
      if (this.$refs.input) {
        this.$refs.input.value = ''
        this.$nextTick(() => this.$refs.input.focus())
      }
    },
    onArrowDown() {
      if (!this.isListVisible) { this.isListVisible = true; return }
      this.activeIndex = Math.min(this.activeIndex + 1, this.items.length - 1)
    },
    onArrowUp() {
      this.activeIndex = Math.max(this.activeIndex - 1, 0)
    },
    selectCurrentSelection() {
      if (this.activeIndex >= 0 && this.items[this.activeIndex]) {
        this.selectItem(this.items[this.activeIndex])
      }
    },
    focus() { this.$refs.input?.focus() },
    select() { this.$refs.input?.select() },
  },
}
</script>
